"use client";

import { useEffect, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Props interface for the LanguageSelect component
 * @interface LanguageSelectProps
 * @property {Function} languageOnChange - Optional callback function triggered when language changes
 */
interface LanguageSelectProps {
  languageOnChange?: (language: string) => void;
}

/**
 * LanguageSelect Component
 *
 * A reusable dropdown component for language selection. It integrates with the
 * application's language context and provides a way to change the current language.
 *
 * @component
 * @param {LanguageSelectProps} props - Component props
 * @returns {JSX.Element} A select dropdown for language selection
 */
export default function LanguageSelect({
  languageOnChange,
}: LanguageSelectProps) {
  // Get language-related values and functions from context
  const { language, setLanguage, allLanguages } = useLanguage();

  /**
   * Effect to set default language when component mounts or when available languages change
   * Sets the first language in the list as default if no language is selected
   */
  useEffect(() => {
    if (allLanguages.length > 0 && !language) {
      const defaultLang = allLanguages[0];
      setLanguage(defaultLang);
      languageOnChange?.(defaultLang);
    }
  }, [allLanguages, language, setLanguage, languageOnChange]);

  /**
   * Handles language change events
   * Updates the language in context and calls the onChange callback if provided
   * @param {string} value - The new language value
   */
  const handleChange = (value: string) => {
    setLanguage(value);
    languageOnChange?.(value);
  };

  // Return early if no languages are available
  if (!allLanguages.length) {
    return <p>No languages available</p>;
  }

  return (
    <Select onValueChange={handleChange} value={language ?? ""}>
      <SelectTrigger className="w-[160px]">
        <span className="text-sm text-gray-500">Language</span>
        <SelectValue placeholder="Select a language" />
      </SelectTrigger>
      <SelectContent>
        {allLanguages.map((lang) => (
          <SelectItem key={lang} value={lang}>
            {lang.toUpperCase()}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
